"use client";

import { useState, useEffect } from "react";
import Link from "next/link";

interface StoreBrand {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  description: string | null;
  product_count: number;
}

export default function LandingPage() {
  const [brands, setBrands] = useState<StoreBrand[]>([]);
  const [shopper, setShopper] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    fetch("/api/store-directory")
      .then((r) => r.json())
      .then((data) => setBrands((data.brands || []).slice(0, 4)))
      .catch(() => {});

    fetch("/api/shopper/me")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.shopper) setShopper(data.shopper);
      })
      .catch(() => {});
  }, []);

  return (
    <>
      {/* ── Header ─────────────────────────────────────── */}
      <header className="site-header">
        <Link href="/" className="site-header__brand">DressingRoom</Link>
        <nav className="site-header__nav">
          <Link href="/" className="site-header__link site-header__link--active">Home</Link>
          <Link href="/store" className="site-header__link">Stores</Link>
          <Link href="/catalog" className="site-header__link">Catalog</Link>
          <Link href="/saved" className="site-header__link">Saved</Link>
          {shopper ? (
            <div className="site-header__auth">
              <span className="site-header__user">{shopper.name}</span>
              <Link href="/profile" className="site-header__link">My Profile</Link>
            </div>
          ) : (
            <div className="site-header__auth">
              <Link href="/login" className="site-header__link">Sign In</Link>
              <Link href="/signup" className="site-header__link site-header__link--cta">Sign Up</Link>
            </div>
          )}
        </nav>
      </header>

      {/* ── Hero ───────────────────────────────────────── */}
      <section className="landing-hero">
        <div className="landing-hero__inner">
          <h1 className="landing-hero__headline">See It Before You Buy It</h1>
          <p className="landing-hero__subheadline">
            AI-powered virtual try-on for Caribbean e-commerce. Know your fit before you order.
            No more guessing. No more returns.
          </p>
          <div className="landing-hero__ctas">
            <Link href="/signup" className="landing-btn landing-btn--primary">
              Start Trying On
            </Link>
            <Link href="/store" className="landing-btn landing-btn--secondary">
              Browse Stores
            </Link>
          </div>
          <div className="landing-hero__trust">
            <span>No credit card needed</span>
            <span className="landing-hero__trust-dot">•</span>
            <span>Works on any device</span>
            <span className="landing-hero__trust-dot">•</span>
            <span>Caribbean-owned</span>
          </div>
        </div>
      </section>

      {/* ── How It Works ───────────────────────────────── */}
      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-section__label">How It Works</div>
          <div className="landing-steps">
            <div className="landing-step">
              <div className="landing-step__number">01</div>
              <div className="landing-step__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="8" r="4" />
                  <path d="M4 22c0-5 4-8 8-8s8 3 8 8" strokeLinecap="round" />
                </svg>
              </div>
              <h3 className="landing-step__title">Create Your Profile</h3>
              <p className="landing-step__desc">
                Enter your measurements, body type, and upload photos. Takes 2 minutes.
              </p>
            </div>

            <div className="landing-step">
              <div className="landing-step__number">02</div>
              <div className="landing-step__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M20.38 3.46L16 2a4 4 0 01-8 0L3.62 3.46a2 2 0 00-1.34 2.23l.58 3.47a1 1 0 00.99.84H6v10c0 1.1.9 2 2 2h8a2 2 0 002-2V10h2.15a1 1 0 00.99-.84l.58-3.47a2 2 0 00-1.34-2.23z" strokeLinejoin="round" />
                  <path d="M12 2v6M9 8h6" strokeLinecap="round" />
                </svg>
              </div>
              <h3 className="landing-step__title">Try On Any Outfit</h3>
              <p className="landing-step__desc">
                Browse Caribbean brands, pick your size and color, see how it actually fits YOUR body.
              </p>
            </div>

            <div className="landing-step">
              <div className="landing-step__number">03</div>
              <div className="landing-step__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h3 className="landing-step__title">Buy With Confidence</h3>
              <p className="landing-step__desc">
                Know it fits before you buy. No more return shipping. No more size guessing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Featured Brands ────────────────────────────── */}
      <section className="landing-section landing-section--alt">
        <div className="landing-container">
          <div className="landing-section__label">Featured Stores</div>
          {brands.length > 0 ? (
            <div className="landing-brands">
              {brands.map((brand) => (
                <Link key={brand.id} href={`/store/${brand.slug}`} className="landing-brand-card">
                  <div className="landing-brand-card__banner">
                    {brand.logo_url ? (
                      <img src={brand.logo_url} alt={brand.name} className="landing-brand-card__logo" />
                    ) : (
                      <span className="landing-brand-card__placeholder">
                        {brand.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <div className="landing-brand-card__body">
                    <h3 className="landing-brand-card__name">{brand.name}</h3>
                    <p className="landing-brand-card__desc">
                      {brand.description || "Premium Caribbean fashion brand"}
                    </p>
                    <div className="landing-brand-card__meta">
                      <span>{brand.product_count} {brand.product_count === 1 ? "piece" : "pieces"}</span>
                      <span className="landing-brand-card__cta">Visit Store →</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="landing-empty-brands">
              <p>Be the first brand on DressingRoom</p>
              <Link href="/brand/signup" className="landing-btn landing-btn--primary">
                Get Started
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ── Value Propositions ─────────────────────────── */}
      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-section__label">Why DressingRoom</div>
          <div className="landing-features">
            <div className="landing-feature">
              <h3 className="landing-feature__title">Honest Fit</h3>
              <p className="landing-feature__desc">
                Our fit engine tells you the truth. If it won&rsquo;t close, we show it won&rsquo;t close.
              </p>
            </div>
            <div className="landing-feature">
              <h3 className="landing-feature__title">Caribbean First</h3>
              <p className="landing-feature__desc">
                Built for Caribbean body types, sizing, and shopping habits. From TTD to USD, we understand the region.
              </p>
            </div>
            <div className="landing-feature">
              <h3 className="landing-feature__title">Shop Local</h3>
              <p className="landing-feature__desc">
                Discover Caribbean brands and designers. Support local businesses.
              </p>
            </div>
            <div className="landing-feature">
              <h3 className="landing-feature__title">Save Money</h3>
              <p className="landing-feature__desc">
                Return shipping costs more than the clothes. Get it right the first time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ──────────────────────────────────────── */}
      <section className="landing-stats">
        <div className="landing-container">
          <div className="landing-stats__row">
            <div className="landing-stat">
              <div className="landing-stat__number">2 min</div>
              <div className="landing-stat__label">Setup</div>
            </div>
            <div className="landing-stat">
              <div className="landing-stat__number">3</div>
              <div className="landing-stat__label">Views per try-on</div>
            </div>
            <div className="landing-stat">
              <div className="landing-stat__number">100%</div>
              <div className="landing-stat__label">Honest fit</div>
            </div>
            <div className="landing-stat">
              <div className="landing-stat__number">$0</div>
              <div className="landing-stat__label">Return shipping</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── For Brands CTA ─────────────────────────────── */}
      <section className="landing-section">
        <div className="landing-container">
          <div className="landing-brands-cta">
            <div className="landing-brands-cta__left">
              <h2 className="landing-brands-cta__headline">Are you a clothing brand?</h2>
              <p className="landing-brands-cta__text">
                Join DressingRoom and let your customers try before they buy.
                Reduce returns, increase conversions, reach more Caribbean shoppers.
              </p>
              <ul className="landing-brands-cta__list">
                <li>Upload your catalog in minutes</li>
                <li>Analytics dashboard</li>
                <li>Starts at $49/mo</li>
              </ul>
            </div>
            <div className="landing-brands-cta__right">
              <Link href="/brand/signup" className="landing-btn landing-btn--primary landing-btn--large">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ─────────────────────────────────────── */}
      <footer className="landing-footer">
        <div className="landing-container">
          <div className="landing-footer__top">
            <div className="landing-footer__brand">DressingRoom</div>
            <nav className="landing-footer__nav">
              <Link href="/profile" className="landing-footer__link">Profile</Link>
              <Link href="/store" className="landing-footer__link">Stores</Link>
              <Link href="/catalog" className="landing-footer__link">Catalog</Link>
              <Link href="/saved" className="landing-footer__link">Saved</Link>
              <Link href="/brand/login" className="landing-footer__link">Brand Login</Link>
              <Link href="/brand/signup" className="landing-footer__link">Brand Signup</Link>
            </nav>
          </div>
          <div className="landing-footer__bottom">
            <p>Powered by AI fit technology</p>
            <p>&copy; 2026 DressingRoom. Caribbean-owned.</p>
          </div>
        </div>
      </footer>

      {/* Mobile nav */}
      <nav className="mobile-nav">
        <Link href="/" className="mobile-nav__btn mobile-nav__btn--active">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M3 10l7-7 7 7v8a1 1 0 01-1 1h-4v-6H8v6H4a1 1 0 01-1-1v-8z" strokeLinejoin="round" />
          </svg>
          Home
        </Link>
        <Link href="/store" className="mobile-nav__btn">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="6" height="6" /><rect x="11" y="3" width="6" height="6" /><rect x="3" y="11" width="6" height="6" /><rect x="11" y="11" width="6" height="6" />
          </svg>
          Stores
        </Link>
        <Link href="/catalog" className="mobile-nav__btn">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="6" height="6" /><rect x="11" y="3" width="6" height="6" /><rect x="3" y="11" width="6" height="6" /><rect x="11" y="11" width="6" height="6" />
          </svg>
          Catalog
        </Link>
        <Link href="/saved" className="mobile-nav__btn">
          <svg className="mobile-nav__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
            <path d="M5 3h10v14l-5-3-5 3V3z" />
          </svg>
          Saved
        </Link>
      </nav>
    </>
  );
}
